import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/ui/radio-group";
import { useAudit } from "../../../../contexts/auditContext";
import { FileText } from "lucide-react";

interface ProcedureSessionSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selectedProcedureSessionIds: string[]) => void;
  documentCount: number;
  documentNames?: string[];
  /**
   * "multiple" puts the same files into every procedure ticked, which is what
   * a loose-file upload does. "single" asks which ONE procedure the upload is
   * for, which is all a zip can honour: a confirmed zip upload carries exactly
   * one procedure_session_id.
   *
   * It is not only about what the request can carry. Answering before the parse
   * means the archive is only ever matched against one procedure's samples, so
   * two procedures numbering their samples from 1 cannot make `Sample 1`
   * ambiguous, and no folder can settle onto a sample the chosen procedure
   * does not own.
   */
  selectionMode?: "single" | "multiple";
  /**
   * Called when the picker is dismissed without an answer - Cancel, Escape, or
   * a click outside. Confirming does NOT call it.
   *
   * `onOpenChange(false)` cannot carry this, because confirming closes the
   * picker too and so fires it as well. Anything the caller staged while
   * waiting for an answer has to be released on dismissal and kept on confirm,
   * which needs the two cases told apart.
   */
  onCancel?: () => void;
}

export function ProcedureSessionSelectionModal({
  open,
  onOpenChange,
  onConfirm,
  documentCount,
  documentNames = [],
  selectionMode = "multiple",
  onCancel,
}: ProcedureSessionSelectionModalProps) {
  const isSingle = selectionMode === "single";
  const { controlSession } = useAudit();
  const [selectedProcedureSessionIds, setSelectedProcedureSessionIds] =
    useState<string[]>([]);

  // The caller keeps one instance of this modal mounted for the life of the
  // page, so without this the answer to the last upload is still selected when
  // the next one opens. In "multiple" mode that was harmless carry-over; in
  // "single" mode it arrives as a pre-answered question with Continue already
  // enabled, and one click files the archive against a procedure nobody chose.
  useEffect(() => {
    if (open) {
      setSelectedProcedureSessionIds([]);
    }
  }, [open]);

  const handleToggleProcedureSession = (procedureSessionId: string) => {
    setSelectedProcedureSessionIds((prev) =>
      prev.includes(procedureSessionId)
        ? prev.filter((id) => id !== procedureSessionId)
        : [...prev, procedureSessionId],
    );
  };

  const handleSelectAll = () => {
    if (!controlSession) return;
    setSelectedProcedureSessionIds(
      controlSession.procedureSessions
        .filter((ps) => ps.procedure.isEnabled)
        .map((ps) => ps.id),
    );
  };

  const handleClearAll = () => {
    setSelectedProcedureSessionIds([]);
  };

  const handleConfirm = () => {
    // A radio group offers one answer, so emit one. The state is a list for
    // both modes, and a list of two leaving a single-choice screen would put
    // the caller's files into a procedure the screen never offered.
    onConfirm(
      isSingle
        ? selectedProcedureSessionIds.slice(0, 1)
        : selectedProcedureSessionIds,
    );
    onOpenChange(false);
  };

  const handleDismiss = () => {
    onCancel?.();
    onOpenChange(false);
  };

  if (!controlSession) return null;

  const enabledProcedureSessions = controlSession.procedureSessions
    .filter((ps) => ps.procedure.isEnabled)
    .sort(
      (a, b) =>
        (a.procedure.procedureOrder || 0) - (b.procedure.procedureOrder || 0),
    );

  const allSelected =
    selectedProcedureSessionIds.length === enabledProcedureSessions.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : handleDismiss())}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isSingle
              ? "Which procedure is this for?"
              : "Add Documents to Procedures"}
          </DialogTitle>
          <DialogDescription>
            {documentCount} document{documentCount > 1 ? "s" : ""} selected
          </DialogDescription>
        </DialogHeader>

        {documentNames.length > 0 && (
          <div className="rounded-lg border bg-stone-50/50 p-4">
            <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-stone-600" />
              Selected Documents
            </h3>
            <div className="max-h-32 overflow-y-auto space-y-2 scrollbar-thin scrollbar-thumb-stone-300 scrollbar-track-stone-100">
              {documentNames.map((name, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-md border border-stone-200 shadow-sm"
                >
                  <FileText className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-stone-700 truncate flex-1">
                    {name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {/* "You can change it later" is true of the archive and false of
                  anything selected alongside it: those upload straight to this
                  answer, and switching procedure in the zip dialog only re-runs
                  the archive's mapping. So the promise is only made when the
                  archive is the only thing riding on it. */}
              {isSingle
                ? documentCount > 1
                  ? "Pick the testing table for these files. The archive's table can still be changed on the next screen."
                  : "Pick the testing table this zip belongs to. You can change it on the next screen."
                : "Select testing tables to add documents to"}
            </p>
            {/* Bulk actions are meaningless when only one may be chosen. */}
            {!isSingle && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={allSelected}
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  disabled={selectedProcedureSessionIds.length === 0}
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>

          {isSingle ? (
            <RadioGroup
              value={selectedProcedureSessionIds[0] ?? ""}
              onValueChange={(value) => setSelectedProcedureSessionIds([value])}
              className="space-y-2 max-h-[400px] overflow-y-auto"
            >
              {enabledProcedureSessions.map((procedureSession) => (
                <div
                  key={procedureSession.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <RadioGroupItem
                    id={procedureSession.id}
                    value={procedureSession.id}
                  />
                  <label
                    htmlFor={procedureSession.id}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium text-sm">
                      {procedureSession.procedure.name}
                    </div>
                  </label>
                </div>
              ))}
            </RadioGroup>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {enabledProcedureSessions.map((procedureSession) => (
                <div
                  key={procedureSession.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-stone-50 transition-colors"
                >
                  <Checkbox
                    id={procedureSession.id}
                    checked={selectedProcedureSessionIds.includes(
                      procedureSession.id,
                    )}
                    onCheckedChange={() =>
                      handleToggleProcedureSession(procedureSession.id)
                    }
                  />
                  <label
                    htmlFor={procedureSession.id}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium text-sm">
                      {procedureSession.procedure.name}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleDismiss}>
            Cancel
          </Button>
          {/* Counted procedures and called them documents. In single mode
              there is nothing to count, so it just moves you on. */}
          <Button
            onClick={handleConfirm}
            disabled={selectedProcedureSessionIds.length === 0}
          >
            {isSingle
              ? "Continue"
              : `Add to ${selectedProcedureSessionIds.length} testing table${
                  selectedProcedureSessionIds.length === 1 ? "" : "s"
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
